import {
    formatPrice, 
    hasParentCurrency, 
    formatNumber, 
    convertAmountToParentCurrency,
    currencyData 
} from "src/MarketDataComponents/Helpers";

import Link from 'next/link'

import {FUND, EQUITY, ETF, MARKET_LSE} from 'src/Api'; 

const TRILLION_VALUE = 1000000000000;
const BILLION_VALUE = 1000000000;
const MILLION_VALUE = 1000000;
const THOUSAND_VALUE = 1000;

export function DrupalLink(props) {
    let baseUrl = process.env.YOUINVEST_BASE_URL;
    const children = props.children;
    const action = props.action;

    if (!baseUrl) { 
        // just a fallback incase its not setup in the secrets manager
        baseUrl = 'https://www.ajbell.co.uk';
    } else if (baseUrl.endsWith("/") && action.startsWith("/")) { 
        // Ensure there are no double slashes e.g .co.uk//action
        baseUrl = baseUrl.substring(0, baseUrl.length - 1);
    }

    return <Link href={`${baseUrl}${props.action}`} {...props}>{children}</Link>
}

export function getBaseUrl() {
    return process.env.YOUINVEST_BASE_URL;
}

export function isProd() {
    // Temporary fix until we can add the env variable to the pipeline
    if (typeof window !== "undefined") {
        return ['beta.ajbell.co.uk', 'www.ajbell.co.uk'].includes(window.location.host);
    }

    return process.env.NODE_ENV === 'production';
}

export function isBeta() {
    if (typeof window !== "undefined") {
        return window.location.host === "beta.ajbell.co.uk";
    }

    return false;
}

export function addCommas(x) {
    let parts = x.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
}

export function penceToPounds(value, decimals, showSymbol) {
    value = value.replace(',','').replace('p','');
    value = value / 100;
    value = value.toFixed(decimals);
    value = (showSymbol ? '£' : '') + value;
    return value;
}
	
export function isStreamingEnabledForInstrument(quote) {
    return (quote && quote.exchangeCode === MARKET_LSE && (quote.currency === 'GBX' || quote.currency === 'GBP'));
}

export function sendCacheHeaders(context, instrument) {
    const {res} = context;

    let maxAge = 14400; // 4 hours 
    

    if (instrument && instrument.instrumentInfo.type == EQUITY) {
        maxAge = 120; // 2 minutes
    } 
    
    res.setHeader(
        'Cache-Control',
        `public, s-maxage=${maxAge}, stale-while-revalidate=${maxAge}`
    )
}

export function formatMarketCap(quote) {
    if (hasParentCurrency(quote.currency) ) {
        const convertedCap = convertAmountToParentCurrency(quote.currency, parseFloat(quote.marketCap));

        if (convertedCap < BILLION_VALUE) {
            return formatPrice(quote.currency, formatNumber((convertedCap/MILLION_VALUE), 2), true) + " mn";
        }

        return formatPrice(quote.currency, formatNumber((convertedCap/BILLION_VALUE), 2), true) + " bn";
    } 

    return formatPrice(quote.currency, formatNumber(parseFloat(quote.marketCap), 2));
}

function displayMarketCap(number, currency, type) {
    if ( !currencyData.hasOwnProperty(currency) ) {
        return `${number} ${type} ${currency}`;
    }
    return formatPrice(currency, number, true) + " " + type;
}

export function alwaysFormatMarketCap(quote) {
    const convertedCap = convertAmountToParentCurrency(quote.currency, parseFloat(quote.marketCap));

    if (convertedCap < BILLION_VALUE) {
        return displayMarketCap(formatNumber((convertedCap/MILLION_VALUE), 2), quote.currency, 'mn');
    } else if (convertedCap < TRILLION_VALUE) {
        return displayMarketCap(formatNumber((convertedCap/BILLION_VALUE), 2), quote.currency, 'bn');
    }
    
    return displayMarketCap(formatNumber((convertedCap/TRILLION_VALUE), 2), quote.currency, 'tn');
}

export function formatLargeNumber(amount) {
    if (Number(amount) === 0) {
        return Number(amount);
    }

    if (Math.abs(amount) <= THOUSAND_VALUE) {
        return amount;
    }

    if (Math.abs(amount) > BILLION_VALUE) {
        return formatNumber((amount/BILLION_VALUE), 2)  + ' bn';
    }

    return formatNumber((amount/MILLION_VALUE), 2) + ' mn';
}

export function formatMillions(number) {
    //if(number > 1000000) {
        //return (number/1000000).toFixed(2)
        return formatNumber(number/MILLION_VALUE, 0)
    //} 
}
export function getTitle(instrumentData, subTitle) {
    const name = getNameType(instrumentData);
    const { quote } = instrumentData;
    return `${name} (${quote.marketCode}) - ${subTitle} | AJ Bell`;
}
export function getDescription(instrumentData) {
    const name = getNameType(instrumentData);
    const {quote} = instrumentData;
    return `View share price quotes, updates and the latest stock news for ${name} (${quote.marketCode}). Explore historical charts, financials and dividends.`;
}
export function getNameType(instrumentData) {
    const {quote} = instrumentData;
    return instrumentData.instrumentInfo.type === ETF ? quote.name : quote.nameShort;
}

export function generateGuid() {
    let d = new Date().getTime();
    if(typeof window !== "undefined" && window.performance && typeof window.performance.now === "function"){
        d += performance.now(); //use high-precision timer if available
    }
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        let r = (d + Math.random()*16)%16 | 0;
        d = Math.floor(d/16);
        return (c=='x' ? r : (r&0x3|0x8)).toString(16);
    });
    return uuid;
}

export function capFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
}

export function renderColor(value, returnValue) {
    let className = '';
    let escapedValue = value;

    try {
        escapedValue = value.replace(/,/g, '');
    } catch (e) {}

    if (escapedValue > 0) {
        className = 'up';
    } else if (escapedValue < 0) {
        className = 'down';
    }

    returnValue = (returnValue) ? returnValue : value;

    return (<span className={className}>{returnValue}</span>);
}

// input date should be YYYY-MM-DDT00:00:00
export function formatDate(date) {
    try {
      if (!date) {
        return '';
      }
   
      const parsedDate = new Date(date);
      if (isNaN(parsedDate)) {
          return '';
      }
         
      return parsedDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
      });
       
      } catch (error) {
        return '';
      }
    }

export function getLatestDividend(dividends) {
    if (Array.isArray(dividends) && dividends.length > 0) {
        const dividend = [...dividends][0];
        const payDate = Date.parse(dividend?.payDate);
        const today = new Date(new Date().setHours(0, 0, 0, 0));
        return isNaN(payDate) || payDate < today ? {} : {
            exDivDate: dividend.exDivDate,
            payDate: dividend.payDate
        };
    }
}
